
import { useState, useEffect , useRef} from "react";
import { Link, useNavigate } from "react-router-dom";
import swal from "sweetalert";
import Loader from "../Common/Loader";
import { useDispatch, useSelector } from "react-redux";

import {
  getNoteCategoriesAction,
  deleteNoteCategoryAction,
} from "../../../store/actions/NoteCategoriesAction";

const NoteCategories = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isFirstLoad = useRef(true);

  const categoryList = useSelector(
    (state) => state.noteCategory.list
  );
  const numRows = useSelector(
    (state) => state.noteCategory.num_rows
  );

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  let searchTimeout = null;

  const [searchName, setSearchName] = useState("");
  const [searchStatus, setSearchStatus] = useState("");

  // Track active search for server-side filtering
  const [activeSearchField, setActiveSearchField] = useState("");
  const [activeSearchKeyword, setActiveSearchKeyword] = useState("");

  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");

  const [currentPage, setCurrentPage] = useState(0);
  const rowsPerPage = 10;

  const fetchCategories = async (
    search_by = "",
    search_keyword = "",
    page = 0,
    sort_by = sortField,
    sort_order = sortOrder
  ) => {
    try {
      // ✅ Show loader only on first page visit
      if (isFirstLoad.current) {
        setLoading(true);
      }
  
      await dispatch(
        getNoteCategoriesAction(
          true,
          page,
          search_by,
          search_keyword,
          sort_by,
          sort_order
        )
      );
    } catch (err) {
      console.error("Failed to fetch categories:", err);
      setError("Failed to load categories.");
    } finally {
      setLoading(false);
      isFirstLoad.current = false; // ✅ stop loader forever
    }
  };
  
  

  useEffect(() => {
    fetchCategories();
  }, []);

  // ================= SEARCH =================
  const handleSearch = (type, value) => {
    if (type === "name") {
      setSearchName(value);
      setSearchStatus("");
    }
  
    if (type === "status") {
      setSearchStatus(value);
      setSearchName("");
    }
  
    setCurrentPage(0);
    setActiveSearchField(type);
    setActiveSearchKeyword(value);
  
    const fieldMap = {
      name: "category_text",
      status: "category_status",
    };
  
    // ✅ Debounce API call
    if (searchTimeout) clearTimeout(searchTimeout);
  
    searchTimeout = setTimeout(() => {
      fetchCategories(
        fieldMap[type],
        value,
        0,
        sortField,
        sortOrder
      );
    }, 400);
  };
  

  // ================= SORT =================
  const handleSort = (field) => {
    const order =
      sortField === field && sortOrder === "asc" ? "desc" : "asc";

    setSortField(field);
    setSortOrder(order);
    setCurrentPage(0); // reset to first page on new sort

    fetchCategories(
      activeSearchField === "name" ? "category_text" : activeSearchField === "status" ? "category_status" : "",
      activeSearchKeyword,
      0,
      field,
      order
    );
  };

  // ================= DELETE =================
  const handleDelete = (id) => {
    swal({
      title: "Are you sure?",
      text: "This category will be deleted permanently",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (yes) => {
      if (yes) {
        try {
          setLoading(true);
          await dispatch(deleteNoteCategoryAction(id));
          // Refresh the current page after deletion
          await fetchCategories(
            activeSearchField === "name" ? "category_text" : activeSearchField === "status" ? "category_status" : "",
            activeSearchKeyword,
            currentPage,
            sortField,
            sortOrder
          );
        } catch (err) {
          setError("Failed to delete category");
          console.error("Delete failed:", err);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  // ================= PAGINATION =================
  const totalPages = Math.ceil(numRows / rowsPerPage);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    fetchCategories(
      activeSearchField === "name" ? "category_text" : activeSearchField === "status" ? "category_status" : "",
      activeSearchKeyword,
      page,
      sortField,
      sortOrder
    );
  };

  return (
    <>
      <div className="row mb-3">
        <div className="col-12 text-end">
          <Link to="/add-note-category" className="btn btn-primary">
            + Add Note Category
          </Link>
        </div>
      </div>

      <div className="card">
        <div className="card-body">
          {error && <div className="text-danger mb-2">{error}</div>}
          {loading ? (
            <div className="text-center my-5">
              <Loader size="lg" />
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <table className="table table-striped">
                  <thead className="table-light">
                    <tr>
                      <th>Seq</th>

                      <th
                        onClick={() => handleSort("category_text")}
                        style={{ cursor: "pointer" }}
                      >
                        Name{" "}
                        {sortField === "category_text" &&
                          (sortOrder === "asc" ? "↑" : "↓")}
                        <input
                          className="form-control form-control-sm mt-1"
                          value={searchName}
                          onChange={(e) =>
                            handleSearch("name", e.target.value)
                          }
                          onClick={(e) => e.stopPropagation()}
                          placeholder="Search name"
                        />
                      </th>

                      <th>Colour</th>

                      <th
                        onClick={() => handleSort("category_status")}
                        style={{ cursor: "pointer" }}
                      >
                        Status{" "}
                        {sortField === "category_status" &&
                          (sortOrder === "asc" ? "↑" : "↓")}
                        <input
                          className="form-control form-control-sm mt-1"
                          value={searchStatus}
                          onChange={(e) =>
                            handleSearch("status", e.target.value)
                          }
                          onClick={(e) => e.stopPropagation()}
                          placeholder="Search status"
                        />
                      </th>

                      <th>Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {categoryList?.length ? (
                      categoryList.map((item) => (
                        <tr key={item.id}>
                          <td>{item.sequence}</td>
                          <td>{item.name}</td>
                          <td>
                            <span
                              style={{
                                background: item.colour,
                                width: "20px",
                                height: "20px",
                                display: "inline-block",
                                borderRadius: "4px",
                              }}
                            />
                          </td>
                          <td>
                            <span
                              className={`badge ${
                                item.is_active ? "bg-success" : "bg-danger"
                              }`}
                            >
                              {item.is_active ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td>
                            <div className="d-flex">
                              <span
                                className="edit me-4"
                                onClick={() =>
                                  navigate("/add-note-category", {
                                    state: {
                                      item,
                                      returnTo: {
                                        currentPage,
                                        searchName,
                                        searchStatus,
                                        sortField,
                                        sortOrder,
                                      },
                                    },
                                  })
                                }
                                style={{ cursor: "pointer" }}
                                title="Edit Category"
                              >
                                <i className="fa fa-pencil-alt text-success" />
                              </span>
                              <span
                                className="delete me-2"
                                onClick={() => handleDelete(item.id)}
                                style={{ cursor: "pointer" }}
                                title="Delete Category"
                              >
                                <i className="fa fa-trash text-danger" />
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="5" className="text-center">
                          No Categories Found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* PAGINATION */}
              <div
                className="pagination-wrapper"
                style={{
                  display: "flex",
                  gap: "6px",
                  alignItems: "center",
                  justifyContent: "center",
                  marginTop: "20px",
                  flexWrap: "wrap",
                  flexDirection: "column",
                }}
              >
                <div>
                  Showing{" "}
                  {categoryList?.length === 0
                    ? 0
                    : currentPage * rowsPerPage + 1}{" "}
                  to{" "}
                  {Math.min(
                    (currentPage + 1) * rowsPerPage,
                    numRows
                  )}{" "}
                  of {numRows} entries
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: "6px",
                    flexWrap: "wrap",
                    justifyContent: "center",
                  }}
                >
                  <button
                    onClick={() =>
                      handlePageChange(Math.max(currentPage - 1, 0))
                    }
                    disabled={currentPage === 0}
                    style={{
                      padding: "6px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      backgroundColor: "#fff",
                      color: "#000",
                      cursor:
                        currentPage === 0 ? "not-allowed" : "pointer",
                      opacity: currentPage === 0 ? 0.5 : 1,
                    }}
                  >
                    &lt;
                  </button>

                  {(() => {
                    const pages = [];
                    const maxPagesToShow = 5;
                    const startPage = Math.max(
                      0,
                      Math.min(
                        currentPage - 2,
                        totalPages - maxPagesToShow
                      )
                    );
                    const endPage = Math.min(
                      startPage + maxPagesToShow,
                      totalPages
                    );

                    if (startPage > 0) {
                      pages.push(
                        <button
                          key={0}
                          onClick={() => handlePageChange(0)}
                          style={{
                            padding: "6px 12px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            backgroundColor: "#fff",
                            color: "#000",
                            cursor: "pointer",
                          }}
                        >
                          1
                        </button>
                      );
                      if (startPage > 1) {
                        pages.push(
                          <span
                            key="dots1"
                            style={{
                              padding: "6px 12px",
                              userSelect: "none",
                            }}
                          >
                            ...
                          </span>
                        );
                      }
                    }

                    for (let i = startPage; i < endPage; i++) {
                      const isActive = currentPage === i;
                      pages.push(
                        <button
                          key={i}
                          onClick={() => handlePageChange(i)}
                          style={{
                            padding: "6px 12px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            backgroundColor: isActive ? "#007bff" : "#fff",
                            color: isActive ? "#fff" : "#000",
                            cursor: "pointer",
                          }}
                        >
                          {i + 1}
                        </button>
                      );
                    }

                    if (endPage < totalPages) {
                      if (endPage < totalPages - 1) {
                        pages.push(
                          <span
                            key="dots2"
                            style={{
                              padding: "6px 12px",
                              userSelect: "none",
                            }}
                          >
                            ...
                          </span>
                        );
                      }
                      pages.push(
                        <button
                          key={totalPages - 1}
                          onClick={() => handlePageChange(totalPages - 1)}
                          style={{
                            padding: "6px 12px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            backgroundColor: "#fff",
                            color: "#000",
                            cursor: "pointer",
                          }}
                        >
                          {totalPages}
                        </button>
                      );
                    }

                    return pages;
                  })()}

                  <button
                    onClick={() =>
                      handlePageChange(
                        Math.min(currentPage + 1, totalPages - 1)
                      )
                    }
                    disabled={currentPage === totalPages - 1}
                    style={{
                      padding: "6px 12px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      backgroundColor: "#fff",
                      color: "#000",
                      cursor:
                        currentPage === totalPages - 1
                          ? "not-allowed"
                          : "pointer",
                      opacity:
                        currentPage === totalPages - 1 ? 0.5 : 1,
                    }}
                  >
                    &gt;
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default NoteCategories;
